import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  User, Mail, Shield, Lock, CheckCircle, AlertCircle, 
  RefreshCw, Eye, EyeOff 
} from 'lucide-react';
import { toast } from 'sonner';

export default function Profile() {
  const { user, setupAlternateEmail, verifyAlternateEmail, changePassword, refreshUser } = useAuth();
  
  // MFA Setup state
  const [alternateEmail, setAlternateEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [mfaStep, setMfaStep] = useState('setup'); // 'setup' | 'verify'
  const [mfaLoading, setMfaLoading] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleSetupMFA = async (e) => {
    e.preventDefault();
    setMfaLoading(true);

    try {
      await setupAlternateEmail(alternateEmail);
      toast.success('Verification code sent to your alternate email');
      setMfaStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMFA = async (e) => {
    e.preventDefault();
    setMfaLoading(true);

    try {
      await verifyAlternateEmail(verificationCode);
      toast.success('MFA enabled successfully!');
      setMfaStep('setup');
      setAlternateEmail('');
      setVerificationCode('');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'employee': return 'bg-blue-100 text-blue-700';
      case 'physician': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return { className: 'bg-emerald-100 text-emerald-700', label: 'Active' };
      case 'pending_approval':
        return { className: 'bg-amber-100 text-amber-700', label: 'Pending Approval' };
      case 'rejected':
        return { className: 'bg-red-100 text-red-700', label: 'Rejected' };
      case 'deactivated':
        return { className: 'bg-slate-100 text-slate-700', label: 'Deactivated' };
      case 'invited':
        return { className: 'bg-blue-100 text-blue-700', label: 'Invited' };
      default:
        return { className: 'bg-slate-100 text-slate-700', label: status || 'Unknown' };
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Profile & Security</h1>
        <p className="text-slate-500 mt-1">Manage your account settings and security preferences</p>
      </div>

      {/* Profile Info */}
      <Card data-testid="profile-info-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-teal-600" />
            Profile Information
          </CardTitle>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">Full Name</Label>
              <p className="text-lg font-medium text-slate-900">
                {user?.first_name} {user?.last_name}
              </p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="text-lg font-medium text-slate-900">{user?.email}</p>
            </div>
            <div>
              <Label className="text-slate-500">Role</Label>
              <div className="mt-1">
                <Badge className={getRoleBadgeColor(user?.role)}>
                  {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-slate-500">Account Status</Label>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusBadge(user?.status).className}>
                  {getStatusBadge(user?.status).label}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MFA Setup */}
      <Card data-testid="mfa-setup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-600" />
            Multi-Factor Authentication (MFA)
          </CardTitle>
          <CardDescription>
            Secure your account with an alternate email verification
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user?.mfa_enabled ? (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-700">
                MFA is enabled. Your account is protected with alternate email verification.
                <br />
                <span className="text-sm text-emerald-600">
                  Alternate email: {user?.alternate_email}
                </span>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  MFA is not enabled. Enable it to add an extra layer of security to your account.
                </AlertDescription>
              </Alert>

              {mfaStep === 'setup' ? (
                <form onSubmit={handleSetupMFA} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="alternate-email">Alternate Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="alternate-email"
                        type="email"
                        placeholder="your.alternate@email.com"
                        value={alternateEmail}
                        onChange={(e) => setAlternateEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="alternate-email-input"
                      />
                    </div>
                    <p className="text-sm text-slate-500">
                      This email will receive OTP codes during login
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={mfaLoading}
                    data-testid="setup-mfa-btn"
                  >
                    {mfaLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Shield className="h-4 w-4 mr-2" />
                    )}
                    Setup MFA
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyMFA} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="verification-code">Verification Code</Label>
                    <Input
                      id="verification-code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      data-testid="verification-code-input"
                    />
                    <p className="text-sm text-slate-500">
                      Enter the code sent to {alternateEmail}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="submit" 
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={mfaLoading || verificationCode.length !== 6}
                      data-testid="verify-mfa-btn"
                    >
                      {mfaLoading ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Verify & Enable MFA
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setMfaStep('setup')}
                      data-testid="cancel-verify-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card data-testid="change-password-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-teal-600" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="current-password"
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="new-password"
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="confirm-password"
                  type={showPasswords ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={passwordLoading}
              data-testid="change-password-btn"
            >
              {passwordLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
